/**
 * Cliente HTTP de la API.
 *
 * La sesión viaja en una cookie httpOnly, así que aquí no hay tokens: solo
 * `credentials: 'same-origin'`. Los errores llegan con la forma que define
 * api/_lib/http.ts, incluidos los errores por campo del formulario.
 */
export interface ApiErrorShape {
  error: string
  code?: string
  fields?: Record<string, string>
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public fields?: Record<string, string>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'No se pudo contactar con el servidor. ¿Está corriendo la API?', 'NETWORK')
  }

  const text = await res.text()
  const payload: unknown = text ? JSON.parse(text) : null

  if (!res.ok) {
    const err = (payload ?? {}) as ApiErrorShape
    throw new ApiError(res.status, err.error ?? `Error ${res.status}`, err.code, err.fields)
  }
  return payload as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  del: <T>(path: string) => request<T>('DELETE', path),
}

// ── Tipos compartidos con la API ────────────────────────────────────────────

export type Role = 'admin' | 'manager' | 'inspector' | 'mechanic'

export interface SessionUser {
  id: string
  email: string
  fullName: string
  roles: Role[]
}

export interface SystemStatus {
  configured: boolean
  source: 'env' | 'file' | 'none'
  editable: boolean
  platform: 'vercel' | 'self-hosted'
  connected: boolean
  latencyMs?: number
  serverVersion?: string
  schemaReady: boolean
  pendingMigrations?: string[]
  driftedMigrations?: string[]
  firstRun: boolean | null
  connection?: Record<string, unknown> | null
  user?: SessionUser | null
  error?: { message: string; code?: string; field?: string }
}

export interface ProbeResponse {
  ok: boolean
  latencyMs: number
  serverVersion?: string
  currentUser?: string
  schemaExists?: boolean
  effectiveSsl?: string
  warnings: string[]
  error?: { message: string; code?: string; field?: string }
}

export const getStatus = () => api.get<SystemStatus>('/setup/status')
export const testConnection = (cfg: unknown) => api.post<ProbeResponse>('/setup/test', cfg)
export const saveConnection = (cfg: unknown) => api.post<{ ok: true }>('/setup/save', cfg)
export const runMigrate = () =>
  api.post<{ ok: true; applied: string[]; skipped: string[]; firstRun: boolean }>('/setup/migrate')
export const bootstrapAdmin = (input: unknown) =>
  api.post<{ ok: true; user: SessionUser }>('/setup/bootstrap-admin', input)
export const login = (input: unknown) => api.post<{ ok: true; user: SessionUser }>('/auth/login', input)
export const logout = () => api.post<{ ok: true }>('/auth/logout')

export interface Vehicle {
  id: string
  unitNumber: string
  vin: string | null
  make: string | null
  model: string | null
  year: number | null
  vehicleType: string
  color: string | null
  licensePlate: string | null
  status: string
  currentOdometer: number
  odometerUnit: string
  acquisitionPrice: number | null
  sunpassNumber: string | null
  primaryPhotoUrl: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

export function listVehicles(params?: { q?: string; type?: string; status?: string }) {
  const sp = new URLSearchParams()
  if (params?.q) sp.set('q', params.q)
  if (params?.type) sp.set('type', params.type)
  if (params?.status) sp.set('status', params.status)
  const qs = sp.toString()
  return api.get<{ vehicles: Vehicle[] }>(`/vehicles${qs ? `?${qs}` : ''}`)
}

export const getVehicle = (id: string) => api.get<{ vehicle: Vehicle }>(`/vehicles/${id}`)
export const createVehicle = (body: unknown) =>
  api.post<{ vehicle: Vehicle }>('/vehicles', body)
export const patchVehicle = (id: string, body: unknown) =>
  api.patch<{ vehicle: Vehicle }>(`/vehicles/${id}`, body)
/**
 * Borra el vehículo. Responde 409 HAS_HISTORY si tiene inspecciones u órdenes
 * de trabajo: en ese caso se retira cambiando su estado, no se borra.
 */
export const deleteVehicle = (id: string) => api.del<{ ok: true }>(`/vehicles/${id}`)

export interface Inspection {
  id: string
  vehicleId: string
  unitNumber: string
  inspectionDatetime: string
  inspectionType: string
  overallStatus: string
  odometer: number | null
  locationText: string | null
  inspectorName: string | null
  notes: string | null
  items?: InspectionItem[]
}

export interface InspectionItem {
  id: string
  category: string
  label: string
  condition: string
  measurementValue: number | null
  measurementUnit: string | null
  notes: string | null
  photoUrl: string | null
  requiresWorkOrder: boolean
  sortOrder: number
}

export function listInspections(params?: {
  q?: string
  type?: string
  status?: string
  vehicleId?: string
}) {
  const sp = new URLSearchParams()
  if (params?.q) sp.set('q', params.q)
  if (params?.type) sp.set('type', params.type)
  if (params?.status) sp.set('status', params.status)
  if (params?.vehicleId) sp.set('vehicleId', params.vehicleId)
  const qs = sp.toString()
  return api.get<{ inspections: Inspection[] }>(`/inspections${qs ? `?${qs}` : ''}`)
}

export const getInspection = (id: string) =>
  api.get<{ inspection: Inspection }>(`/inspections/${id}`)
export const patchInspection = (id: string, body: unknown) =>
  api.patch<{ inspection: unknown }>(`/inspections/${id}`, body)
export const deleteInspection = (id: string) => api.del<{ ok: true }>(`/inspections/${id}`)

export const createInspection = (body: unknown) =>
  api.post<{ inspection: Inspection }>('/inspections', body)

export interface ChecklistTemplate {
  id: string
  name: string
  inspectionType: string
  items: {
    category: string
    label: string
    measurementUnit: string | null
    requiresMeasurement: boolean
    sortOrder: number
  }[]
}

export const listChecklistTemplates = () =>
  api.get<{ templates: ChecklistTemplate[] }>('/checklist-templates')

export interface WorkOrder {
  id: string
  workOrderNumber: string
  vehicleId: string
  unitNumber: string
  title: string
  status: string
  priority: string
  dateOpened: string
  dateCompleted: string | null
  totalCost: number
  vendorId: string | null
  vendorName: string | null
  description?: string | null
  notes?: string | null
  invoiceNumber?: string | null
  poNumber?: string | null
  subtotal?: number
  tax?: number
  maintenanceType?: string
  items?: WorkOrderItem[]
}

export interface WorkOrderItem {
  id: string
  itemType: string
  description: string
  partNumber: string | null
  quantity: number
  unitCost: number
  lineTotal: number
  sortOrder: number
}

export function listWorkOrders(params?: {
  q?: string
  status?: string
  priority?: string
  vehicleId?: string
}) {
  const sp = new URLSearchParams()
  if (params?.q) sp.set('q', params.q)
  if (params?.status) sp.set('status', params.status)
  if (params?.priority) sp.set('priority', params.priority)
  if (params?.vehicleId) sp.set('vehicleId', params.vehicleId)
  const qs = sp.toString()
  return api.get<{ workOrders: WorkOrder[] }>(`/work-orders${qs ? `?${qs}` : ''}`)
}

export const getWorkOrder = (id: string) =>
  api.get<{ workOrder: WorkOrder }>(`/work-orders/${id}`)
export const createWorkOrder = (body: unknown) =>
  api.post<{ workOrder: WorkOrder }>('/work-orders', body)
export const deleteWorkOrder = (id: string) => api.del<{ ok: true }>(`/work-orders/${id}`)
export const patchWorkOrder = (id: string, body: unknown) =>
  api.patch<{ workOrder: WorkOrder }>(`/work-orders/${id}`, body)

export interface Vendor {
  id: string
  companyName: string
  contactName: string | null
  phone: string | null
  email: string | null
  city: string | null
  vendorType: string
  active: boolean
  notes: string | null
}

export function listVendors(params?: { q?: string }) {
  const sp = new URLSearchParams()
  if (params?.q) sp.set('q', params.q)
  const qs = sp.toString()
  return api.get<{ vendors: Vendor[] }>(`/vendors${qs ? `?${qs}` : ''}`)
}

export const createVendor = (body: unknown) => api.post<{ vendor: Vendor }>('/vendors', body)
export const getVendor = (id: string) => api.get<{ vendor: Vendor }>(`/vendors/${id}`)
export const patchVendor = (id: string, body: unknown) =>
  api.patch<{ vendor: Vendor }>(`/vendors/${id}`, body)
/**
 * DESACTIVA el proveedor (active = false); no lo borra. El historial de
 * órdenes y gastos lo referencia, y perder esa atribución sería peor que
 * mantener una ficha inactiva. Se revierte con patchVendor(id, { active: true }).
 */
export const deactivateVendor = (id: string) => api.del<{ vendor: Vendor }>(`/vendors/${id}`)

export interface Expense {
  id: string
  date: string
  amount: number
  description: string | null
  reference: string | null
  vehicleId: string | null
  unitNumber: string | null
  vendorId: string | null
  vendorName: string | null
  categoryId: string | null
  categoryName: string | null
}

export function listExpenses(params?: { vehicleId?: string }) {
  const sp = new URLSearchParams()
  if (params?.vehicleId) sp.set('vehicleId', params.vehicleId)
  const qs = sp.toString()
  return api.get<{ expenses: Expense[]; total: number }>(`/expenses${qs ? `?${qs}` : ''}`)
}

export const patchExpense = (id: string, body: unknown) =>
  api.patch<{ expense: unknown }>(`/expenses/${id}`, body)
export const deleteExpense = (id: string) => api.del<{ ok: true }>(`/expenses/${id}`)

export const createExpense = (body: unknown) => api.post<{ expense: Expense }>('/expenses', body)

export const listExpenseCategories = () =>
  api.get<{ categories: { id: string; name: string; isRepair: boolean }[] }>(
    '/expense-categories',
  )

export interface MaintenanceSchedule {
  id: string
  vehicleId: string
  unitNumber: string
  maintenanceName: string
  maintenanceType: string
  nextDueDate: string | null
  nextDueOdometer: number | null
  currentOdometer: number
  odometerUnit: string
  distanceRemaining: number | null
  daysRemaining: number | null
  dueState: string
}

export function listMaintenance(params?: { vehicleId?: string }) {
  const sp = new URLSearchParams()
  if (params?.vehicleId) sp.set('vehicleId', params.vehicleId)
  const qs = sp.toString()
  return api.get<{ schedules: MaintenanceSchedule[] }>(
    `/maintenance${qs ? `?${qs}` : ''}`,
  )
}

export const patchMaintenance = (id: string, body: unknown) =>
  api.patch<{ maintenance: unknown }>(`/maintenance/${id}`, body)
export const deleteMaintenance = (id: string) => api.del<{ ok: true }>(`/maintenance/${id}`)

export const createMaintenance = (body: unknown) => api.post<{ id: string }>('/maintenance', body)

export interface DashboardData {
  kpis: {
    activeVehicles: number
    totalVehicles: number
    openWorkOrders: number
    failedInspections30d: number
    cost12m: number
  }
  monthlyCost: { month: string; repairs: number; other: number }[]
  fleetByType: { type: string; count: number }[]
  topCostUnits: { unitNumber: string; total: number }[]
  maintenanceAlerts: { unitNumber: string; name: string; dueState: string }[]
  priorityRepairs: {
    id: string
    workOrderNumber: string
    title: string
    unitNumber: string
    priority: string
  }[]
  failedInspections: {
    id: string
    unitNumber: string
    status: string
    datetime: string
  }[]
  fleetStatus: { status: string; count: number }[]
}

export const getDashboard = () => api.get<DashboardData>('/dashboard')

export interface ReportUnit {
  vehicleId: string
  unitNumber: string
  vehicleType: string
  currentOdometer: number
  odometerUnit: string
  repairs: number
  other: number
  total: number
  costPerUnitDistance: number | null
}

export const getReports = () =>
  api.get<{
    kpis: { units: number; repairs: number; other: number; total: number }
    units: ReportUnit[]
  }>('/reports')

export const createVehicleComponent = (body: unknown) =>
  api.post<{ id: string }>('/vehicle-components', body)
export const patchVehicleComponent = (id: string, body: unknown) =>
  api.patch<{ component: unknown }>(`/vehicle-components/${id}`, body)
export const deleteVehicleComponent = (id: string) =>
  api.del<{ ok: true }>(`/vehicle-components/${id}`)

export function listVehicleComponents(vehicleId: string) {
  return api.get<{
    components: {
      id: string
      componentType: string
      name: string | null
      position: string | null
      serialNumber: string | null
      installationDate: string | null
      cost: number | null
      notes: string | null
    }[]
  }>(`/vehicle-components?vehicleId=${encodeURIComponent(vehicleId)}`)
}

export interface OdometerReading {
  id: string
  date: string
  reading: number
  /** 'Manual' | 'Inspection' | 'Work Order' | 'Expense' | 'Import' */
  source: string
  /** Id de la inspección u orden que la originó, si la lectura no es manual. */
  sourceId: string | null
  notes: string | null
}

export function listOdometerHistory(vehicleId: string) {
  return api.get<{ readings: OdometerReading[] }>(
    `/odometer-history?vehicleId=${encodeURIComponent(vehicleId)}`,
  )
}

export const createOdometerReading = (body: unknown) =>
  api.post<{ id: string }>('/odometer-history', body)

/**
 * Corrige una lectura. La respuesta trae además cómo queda el odómetro del
 * vehículo: el servidor lo recalcula porque el trigger del esquema solo actúa
 * al insertar y solo hacia arriba.
 */
export const patchOdometerReading = (id: string, body: unknown) =>
  api.patch<{ reading: OdometerReading; vehicle: OdometerSync }>(`/odometer-history/${id}`, body)
export const deleteOdometerReading = (id: string) =>
  api.del<{ ok: true; vehicle: OdometerSync }>(`/odometer-history/${id}`)

export interface OdometerSync {
  currentOdometer: number
  /** false = al vehículo ya no le queda ninguna lectura que respalde ese número. */
  hasReadings: boolean
}

// ── Usuarios y roles ────────────────────────────────────────────────────────

export interface ManagedUser {
  id: string
  email: string
  fullName: string
  phone: string | null
  active: boolean
  roles: Role[]
  lastLoginAt: string | null
  createdAt: string
}

export const listUsers = () => api.get<{ users: ManagedUser[] }>('/users')
export const createUser = (body: unknown) => api.post<{ user: ManagedUser }>('/users', body)
export const patchUser = (id: string, body: unknown) =>
  api.patch<{ user: ManagedUser }>(`/users/${id}`, body)
/**
 * DESACTIVA al usuario; no lo borra. Inspecciones, lecturas y adjuntos lo
 * referencian con ON DELETE SET NULL, y borrarlo dejaría el historial sin decir
 * quién hizo cada cosa. Se revierte con patchUser(id, { active: true }).
 */
export const deactivateUser = (id: string) => api.del<{ user: ManagedUser }>(`/users/${id}`)

// ── Correo de recordatorios ─────────────────────────────────────────────────

export interface EmailSettingsView {
  enabled: boolean
  user: string
  fromName: string
  recipients: string[]
  /** La contraseña NUNCA viaja al navegador; solo si hay una guardada. */
  hasPassword: boolean
}

export interface GmailOauthStatus {
  /** Hay GMAIL_CLIENT_ID / SECRET / REDIRECT_URI en el servidor. */
  configured: boolean
  connected: boolean
  connectedEmail: string | null
  scope: string | null
  connectedAt: string | null
  expiresAt: string | null
}

export const getEmailSettings = () =>
  api.get<{
    gmail: GmailOauthStatus
    settings: EmailSettingsView | null
    effectiveRecipients: string[]
  }>('/settings/email')

/** Desconecta solo la cuenta OAuth; conserva destinatarios y remitente. */
export const disconnectGmail = () => api.del<{ ok: true }>('/settings/email?gmail=1')

export const saveEmailSettings = (body: unknown) =>
  request<{ settings: EmailSettingsView; effectiveRecipients: string[] }>(
    'PUT',
    '/settings/email',
    body,
  )

export const deleteEmailSettings = () => api.del<{ ok: true }>('/settings/email')

/** send=false solo autentica; send=true manda un correo real a la lista. */
export const testEmailSettings = (send: boolean) =>
  api.post<{ ok: boolean; error?: string; recipients?: string[] }>(
    `/settings/email${send ? '?send=1' : ''}`,
  )
